import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import PageLayout from '@/components/shared/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  UserPlus,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Activity,
  Search,
  X,
  CalendarDays,
  Users,
  Smartphone,
  Monitor,
  Filter,
  Link2,
  ClipboardList,
  TrendingDown,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import type { RealFunnelMetrics } from '@/lib/services/funnel-analytics-service';
import { formatDateTimeLocale } from '@/lib/utils';
import logger from '@/lib/logging';
import SelfAssessmentReport from '@/components/posture-analysis/self-assessment/SelfAssessmentReport';
import type { PostureAnalysis } from '@/lib/types';
import { fetchProspectById, fetchProspects } from '@/lib/prospects-api-client';
import {
  getGhlCalendarStatus,
  salesProspectPath,
  type GhlCalendarStatus,
  type GhlSurveySnapshot,
  type ProspectListItem,
} from '@/lib/prospects-model';
import { salesProspectAbsoluteUrl } from '@/lib/sales-prospect-ghl-attach';
import { getGroupKeyAndLabel, isSameLocalCalendarDay, type ProspectGroupByMode } from '@/lib/prospects-grouping';
import { ProspectGhlBookingCard } from '@/components/admin/ProspectGhlBookingCard';
import { hasGhlSelectedSlot, type GhlBookingRecord } from '@/lib/ghl-booking';

interface ClientProspect {
  id: string;
  name: string;
  email: string;
  gdprConsent: boolean;
  pictureConsent?: boolean;
  deviceType?: 'mobile' | 'desktop';
  userAgent?: string;
  createdAt: any;
  ghlCalendarOpenedAt?: string | null;
  ghlCalendarBookedAt?: string | null;
  ghlSurvey?: GhlSurveySnapshot;
  ghlBooking?: GhlBookingRecord;
  ghlBookedAt?: string | null;
  source?: string;
}

function toClientProspect(item: ProspectListItem): ClientProspect {
  return {
    id: item.id,
    name: item.name,
    email: item.email,
    gdprConsent: item.gdprConsent,
    pictureConsent: item.pictureConsent === true,
    deviceType: item.deviceType,
    userAgent: item.userAgent,
    createdAt: item.createdAt,
    ghlCalendarOpenedAt: item.ghlCalendarOpenedAt,
    ghlCalendarBookedAt: item.ghlCalendarBookedAt,
    ghlSurvey: item.ghlSurvey,
    ghlBooking: item.ghlBooking,
    ghlBookedAt: item.ghlBookedAt,
    source: item.source,
  };
}

function GhlCalendarStatusBadges({ status }: { status: GhlCalendarStatus }) {
  if (status === 'none') {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="inline-flex flex-wrap items-center gap-1.5">
      <Badge variant="outline" className="text-muted-foreground border-border text-xs px-2.5 py-0.5 rounded-full">
        Opened
      </Badge>
      {status === 'booked' ? (
        <Badge
          variant="secondary"
          className="bg-success-surface text-success-text border-none text-xs px-2.5 py-0.5 rounded-full"
        >
          Booked
        </Badge>
      ) : null}
    </div>
  );
}

type GroupByMode = ProspectGroupByMode;
type DeviceFilterMode = 'all' | 'mobile' | 'desktop';

interface ProspectGroup {
  key: string;
  label: string;
  prospects: ClientProspect[];
}

function getProspectDevice(prospect: ClientProspect): 'mobile' | 'desktop' {
  if (prospect.deviceType === 'mobile' || prospect.deviceType === 'desktop') {
    return prospect.deviceType;
  }
  if (prospect.userAgent) {
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(prospect.userAgent);
    return isMobile ? 'mobile' : 'desktop';
  }
  return 'desktop';
}

function getProspectDate(prospect: ClientProspect): Date | null {
  if (!prospect.createdAt) return null;
  if (typeof prospect.createdAt.toDate === 'function') {
    return prospect.createdAt.toDate();
  }
  const d = new Date(prospect.createdAt);
  return Number.isNaN(d.getTime()) ? null : d;
}

interface ProspectListProps {
  prospects: ClientProspect[];
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  loadError: string | null;
  onRetry: () => void;
}

const ProspectList: React.FC<ProspectListProps> = ({
  prospects,
  isLoading,
  searchQuery,
  onSearchChange,
  loadError,
  onRetry,
}) => {
  const [groupBy, setGroupBy] = useState<GroupByMode>('day');
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilterMode>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const filtered = useMemo(() => {
    return prospects.filter((p) => {
      const device = getProspectDevice(p);
      return (
        deviceFilter === 'all' ||
        (deviceFilter === 'mobile' && device === 'mobile') ||
        (deviceFilter === 'desktop' && device === 'desktop')
      );
    });
  }, [prospects, deviceFilter]);

  const kpis = useMemo(() => {
    const now = new Date();
    let today = 0;
    let mobileCount = 0;
    let desktopCount = 0;
    prospects.forEach((p) => {
      const d = getProspectDate(p);
      if (d && isSameLocalCalendarDay(d, now)) today += 1;
      if (getProspectDevice(p) === 'mobile') mobileCount += 1;
      else desktopCount += 1;
    });
    const total = prospects.length || 1;
    return {
      total: prospects.length,
      today,
      mobileCount,
      desktopCount,
      mobilePct: Math.round((mobileCount / total) * 100),
      desktopPct: Math.round((desktopCount / total) * 100),
    };
  }, [prospects]);

  const groups = useMemo(() => {
    const groupMap = new Map<string, ProspectGroup>();
    filtered.forEach((p) => {
      const d = getProspectDate(p);
      const { key, label } = d ? getGroupKeyAndLabel(d, groupBy) : { key: 'unknown', label: 'Unknown Date' };
      if (!groupMap.has(key)) groupMap.set(key, { key, label, prospects: [] });
      groupMap.get(key)!.prospects.push(p);
    });
    return Array.from(groupMap.values());
  }, [filtered, groupBy]);

  const searchField = (
    <div className="relative flex-1 max-w-sm space-y-1">
      <Label htmlFor="prospect-email-search">Find by email</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          id="prospect-email-search"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Type the email address."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full h-12 pl-9 pr-12 bg-background border border-input rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        />
        {searchQuery && (
          <button
            type="button"
            aria-label="Clear email search"
            onClick={() => onSearchChange('')}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className="sr-only" aria-live="polite">
        {filtered.length === 1 ? '1 person found' : `${filtered.length} people found`}
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-center py-16 text-muted-foreground space-y-3">
        <p className="text-base font-medium text-foreground">Could not load the list. Try again.</p>
        <p className="text-xs font-mono">{loadError}</p>
        <Button type="button" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (prospects.length === 0) {
    return (
      <div className="space-y-4">
        {searchField}
        <div className="text-center py-16 text-muted-foreground">
          <UserPlus className="mx-auto h-10 w-10 mb-3 opacity-40" />
          <p className="text-base font-medium">
            {searchQuery ? 'No one matches that email. Check spelling, then try again.' : 'No new clients yet'}
          </p>
          <p className="text-sm mt-1">
            {searchQuery
              ? 'Clear the search to see everyone again.'
              : (
                <>
                  They appear after someone finishes the web form at{' '}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">/self-assessment</code>.
                </>
              )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border border-border rounded-xl bg-card/60">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Leads</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">{kpis.total}</p>
            </div>
            <Users className="h-7 w-7 text-primary/80" />
          </CardContent>
        </Card>
        <Card className="border border-border rounded-xl bg-card/60">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Today</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">{kpis.today}</p>
            </div>
            <CalendarDays className="h-7 w-7 text-primary/80" />
          </CardContent>
        </Card>
        <Card className="border border-border rounded-xl bg-card/60">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">
                {kpis.mobileCount} <span className="text-xs font-normal text-muted-foreground">({kpis.mobilePct}%)</span>
              </p>
            </div>
            <Smartphone className="h-7 w-7 text-primary/80" />
          </CardContent>
        </Card>
        <Card className="border border-border rounded-xl bg-card/60">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Computer</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">
                {kpis.desktopCount} <span className="text-xs font-normal text-muted-foreground">({kpis.desktopPct}%)</span>
              </p>
            </div>
            <Monitor className="h-7 w-7 text-primary/80" />
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {searchField}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Filter className="h-3 w-3" /> Device:
            </span>
            <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1 text-xs" role="radiogroup" aria-label="Device filter">
              {(['all', 'mobile', 'desktop'] as DeviceFilterMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={deviceFilter === mode}
                  onClick={() => setDeviceFilter(mode)}
                  className={`px-3 py-1.5 min-h-11 rounded-md font-medium ${
                    deviceFilter === mode ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
                  }`}
                >
                  {mode === 'all' ? 'All' : mode === 'mobile' ? 'Phone' : 'Computer'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border rounded-xl">
          <p className="text-base font-medium">No matching people found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = Boolean(collapsedGroups[group.key]);
            return (
              <div key={group.key} className="border border-border rounded-xl overflow-hidden bg-card">
                {groupBy !== 'none' && (
                  <button
                    type="button"
                    aria-expanded={!isCollapsed}
                    onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 border-b border-border"
                  >
                    <div className="flex items-center gap-2 font-headline font-semibold text-sm text-foreground">
                      {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      <span>{group.label}</span>
                    </div>
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-none font-mono text-xs">
                      {group.prospects.length}
                    </Badge>
                  </button>
                )}
                {!isCollapsed && (
                  <div className="overflow-x-auto md:overflow-visible">
                    <div className="md:hidden space-y-3 p-3">
                      {group.prospects.map((p) => (
                        <Link
                          key={p.id}
                          to={salesProspectPath(p.id)}
                          className="block rounded-lg border border-border p-4 min-h-11"
                        >
                          <div className="font-semibold text-foreground">{p.name}</div>
                          <div className="text-sm text-muted-foreground break-all">{p.email}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {p.createdAt ? formatDateTimeLocale(p.createdAt.toDate?.() ?? p.createdAt) : '—'}
                          </div>
                          <div className="mt-2">
                            <GhlCalendarStatusBadges status={getGhlCalendarStatus(p)} />
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Table className="hidden md:table">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Calendar</TableHead>
                          <TableHead>GHL slot</TableHead>
                          <TableHead>Submitted</TableHead>
                          <TableHead className="text-right">Consent</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.prospects.map((p) => (
                          <TableRow key={p.id} className="h-12">
                            <TableCell className="font-semibold">
                              <Link
                                to={salesProspectPath(p.id)}
                                className="text-primary underline-offset-4 hover:underline min-h-11 inline-flex items-center"
                              >
                                {p.name}
                              </Link>
                            </TableCell>
                            <TableCell>{p.email}</TableCell>
                            <TableCell>{getProspectDevice(p) === 'mobile' ? 'Phone' : 'Computer'}</TableCell>
                            <TableCell>
                              <GhlCalendarStatusBadges status={getGhlCalendarStatus(p)} />
                            </TableCell>
                            <TableCell>
                              {hasGhlSelectedSlot(p.ghlBooking) ? (
                                <Badge
                                  variant="outline"
                                  className="bg-primary/10 text-primary border-primary/20 text-xs px-2.5 py-0.5 rounded-full"
                                >
                                  Booked
                                </Badge>
                              ) : p.ghlBooking ? (
                                <Badge
                                  variant="outline"
                                  className="text-muted-foreground border-border text-xs px-2.5 py-0.5 rounded-full"
                                >
                                  Pending slot
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {p.createdAt ? formatDateTimeLocale(p.createdAt.toDate?.() ?? p.createdAt) : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                                <span>{p.gdprConsent ? 'Said yes to contact' : 'Did not agree to contact'}</span>
                                {p.pictureConsent ? (
                                  <Badge
                                    variant="secondary"
                                    className="bg-success-surface text-success-text border-none text-xs px-2.5 py-0.5 rounded-full"
                                  >
                                    Photos
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-muted-foreground border-border text-xs px-2.5 py-0.5 rounded-full"
                                  >
                                    No photos
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface ProspectDetailProps {
  prospect: ClientProspect;
  analyses: PostureAnalysis[];
  isLoading: boolean;
  onBack: () => void;
}

const ProspectDetail: React.FC<ProspectDetailProps> = ({ prospect, analyses, isLoading, onBack }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const calendarStatus = getGhlCalendarStatus(prospect);

  const handleCopyLink = useCallback(async () => {
    const url = salesProspectAbsoluteUrl(window.location.origin, prospect.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: 'Link copied' });
    } catch (err) {
      logger.error('[SalesProspects] Failed to copy prospect link:', err);
    }
  }, [prospect.id, toast]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" />
          Back to list
        </Button>
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={handleCopyLink} aria-label="Copy link for this person">
          <Link2 className="h-4 w-4" />
          Copy link for this person
        </Button>
        <Button variant="outline" size="sm" className="gap-1" asChild>
          <Link
            to={`/clients/new?name=${encodeURIComponent(prospect.name)}&email=${encodeURIComponent(prospect.email)}&prospectId=${encodeURIComponent(prospect.id)}`}
          >
            <UserPlus className="h-4 w-4" />
            New Client
          </Link>
        </Button>
        <span className="sr-only" aria-live="polite">
          {copied ? 'Link copied' : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <Card className="lg:col-span-1 border border-border rounded-xl bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 font-headline tracking-tight text-foreground">
              <UserPlus className="h-4 w-4 text-primary" />
              {prospect.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm pt-2">
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Email</div>
              <div className="text-foreground font-medium text-base break-all">{prospect.email}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Submitted</div>
              <div className="text-foreground font-medium">
                {prospect.createdAt ? formatDateTimeLocale(prospect.createdAt.toDate?.() ?? prospect.createdAt) : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Contact</div>
              <div>{prospect.gdprConsent ? 'Said yes to contact' : 'Did not agree to contact'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Photo Consent</div>
              <div>
                {prospect.pictureConsent ? (
                  <Badge variant="secondary" className="bg-success-surface text-success-text border-none text-xs px-2.5 py-0.5 rounded-full">
                    Photos
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground border-border text-xs px-2.5 py-0.5 rounded-full">
                    No photos
                  </Badge>
                )}
              </div>
            </div>
            {prospect.ghlBooking && <ProspectGhlBookingCard booking={prospect.ghlBooking} />}
            <div className="pt-2 border-t border-border space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Calendar
              </div>
              <GhlCalendarStatusBadges status={calendarStatus} />
              {calendarStatus === 'none' ? (
                <p className="text-xs text-muted-foreground">Has not opened the GHL booking calendar.</p>
              ) : calendarStatus === 'opened' ? (
                <p className="text-xs text-muted-foreground">Opened the GHL booking calendar. Booking not confirmed.</p>
              ) : (
                <p className="text-xs text-muted-foreground">Opened the GHL booking calendar and booked an appointment.</p>
              )}
            </div>
            <div className="pt-2 border-t border-border space-y-3">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <ClipboardList className="h-3.5 w-3.5" />
                Survey
              </div>
              {prospect.ghlSurvey?.answers?.length ? (
                prospect.ghlSurvey.answers.map((qa) => (
                  <div key={`${qa.question}-${qa.answer}`}>

                    <div className="text-xs font-semibold text-muted-foreground mb-0.5">{qa.question}</div>
                    <div className="text-foreground font-medium">{qa.answer || '—'}</div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">No survey answers captured yet.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          {isLoading && <Skeleton className="h-40 w-full rounded-xl" />}
          {!isLoading && analyses.length === 0 && (
            <Card className="border border-border rounded-xl">
              <CardContent className="text-center py-12 text-muted-foreground">
                <Activity className="mx-auto h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm font-medium">No posture analysis stored for this person.</p>
              </CardContent>
            </Card>
          )}
          {!isLoading &&
            analyses.map((analysis) => (
              <Card key={analysis.id} className="border border-border rounded-xl overflow-hidden bg-card">
                <CardHeader className="pb-3 bg-muted/20 border-b border-border">
                  <CardTitle className="text-sm text-muted-foreground font-medium">Posture Analysis Report</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <SelfAssessmentReport analysis={analysis} followAppTheme />
                </CardContent>
              </Card>
            ))}
        </div>
      </div>
    </div>
  );
};

interface FunnelStage {
  label: string;
  value: number;
  color: string;
}

const FunnelSummaryCard: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<RealFunnelMetrics | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (data) return;
    setLoading(true);
    try {
      const metrics = await apiFetch<RealFunnelMetrics>('/admin/funnel-analytics', { cache: 'no-store' });
      setData(metrics);
    } catch {
      setData({});
    } finally {
      setLoading(false);
    }
  }, [data]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const stages: FunnelStage[] = data
    ? [
        { label: 'Page Views', value: data.initialTraffic, color: 'text-blue-500' },
        { label: 'Photos Uploaded', value: data.pictureUploadSuccess, color: 'text-emerald-500' },
        { label: 'Analyzed', value: data.analysisSucceeded ?? 0, color: 'text-lime-600' },
        { label: 'Survey Opened', value: data.surveyOpened ?? 0, color: 'text-violet-500' },
        { label: 'Survey Submitted', value: data.surveySubmitted ?? 0, color: 'text-teal-500' },
        { label: 'Calendar Opened', value: data.bookingCalendarOpened ?? 0, color: 'text-amber-500' },
        { label: 'Appt. Booked', value: data.walkthroughCallBooked, color: 'text-cyan-500' },
      ]
    : [];

  return (
    <Card className="mb-4 border-border/60">
      <CardHeader
        className="py-3 px-4 cursor-pointer select-none flex flex-row items-center justify-between"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold text-foreground">Funnel Overview</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/sales/funnel"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            Full analytics <ExternalLink className="h-3 w-3" />
          </Link>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </CardHeader>

      {open && (
        <CardContent className="pb-4 px-4">
          {loading ? (
            <div className="flex gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 flex-1 rounded-lg" />
              ))}
            </div>
          ) : stages.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              {stages.map((stage, idx) => (
                <React.Fragment key={stage.label}>
                  <div className="flex flex-col items-center px-3 py-2 rounded-lg bg-muted/40 border border-border min-w-[80px] text-center">
                    <span className={`text-xl font-black ${stage.color}`}>{stage.value.toLocaleString()}</span>
                    <span className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{stage.label}</span>
                  </div>
                  {idx < stages.length - 1 && (
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No funnel data yet.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export const AdminProspectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: prospectId } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const emailQuery = searchParams.get('email') ?? '';

  const [prospects, setProspects] = useState<ClientProspect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ClientProspect | null>(null);
  const [analyses, setAnalyses] = useState<PostureAnalysis[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadProspects = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await fetchProspects(emailQuery || undefined);
      setProspects(result.items.map(toClientProspect));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not load the list.';
      logger.error('[SalesProspects] Failed to load prospects:', err);
      setLoadError(message);
      setProspects([]);
    } finally {
      setIsLoading(false);
    }
  }, [emailQuery]);

  useEffect(() => {
    if (prospectId) return;
    loadProspects();
  }, [loadProspects, prospectId]);

  useEffect(() => {
    if (!prospectId) {
      setDetail(null);
      setAnalyses([]);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setNotFound(false);
    fetchProspectById(prospectId)
      .then((record) => {
        if (cancelled) return;
        setDetail(toClientProspect(record));
        setAnalyses((record.postureAnalyses || []) as PostureAnalysis[]);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('[SalesProspects] Failed to load prospect detail:', err);
        setNotFound(true);
        setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  const handleSearchChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set('email', value);
    else next.delete('email');
    setSearchParams(next, { replace: true });
  };

  if (prospectId && notFound) {
    return (
      <PageLayout title="This link does not open a person">
        <Card>
          <CardContent className="pt-6 space-y-3">
            <p className="text-muted-foreground">Ask for a new link, or go back to the list.</p>
            <Button type="button" onClick={() => navigate('/sales/prospects')}>
              Back to list
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  if (prospectId) {
    return (
      <PageLayout title={detail ? `${detail.name} — new client` : 'New client'}>
        {detailLoading && !detail ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : detail ? (
          <ProspectDetail
            prospect={detail}
            analyses={analyses}
            isLoading={detailLoading}
            onBack={() => navigate('/sales/prospects')}
          />
        ) : null}
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Prospects" subtitle="Find people who completed the web form">
      <FunnelSummaryCard />
      <Card>
        <CardContent className="pt-4">
          <ProspectList
            prospects={prospects}
            isLoading={isLoading}
            searchQuery={emailQuery}
            onSearchChange={handleSearchChange}
            loadError={loadError}
            onRetry={loadProspects}
          />
        </CardContent>
      </Card>
    </PageLayout>
  );
};

export default AdminProspectsPage;
